import React, { useEffect, useState } from 'react';
import { Translate, translate } from 'react-jhipster';
import { useAppDispatch, useAppSelector } from 'app/config/store';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import ExpandLessRoundedIcon from '@mui/icons-material/ExpandLessRounded';
import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded';
import Collapse from '@mui/material/Collapse';
import Box from '@mui/material/Box';
import FormControl from '@mui/material/FormControl';
import { Button, Input, Label } from 'reactstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { updateEntity as updateBank } from 'app/entities/bank/bank.reducer';
import { getEntities as getPartners } from 'app/entities/partner/partner.reducer';
import { AUTHORITIES } from 'app/config/constants';

// International commodity uses partnerId / partnerIdApi; Local commodity uses localPartnerId / localPartnerIdApi.
const PARTNER_FIELDS = {
  International: { website: 'partnerId', api: 'partnerIdApi' },
  Local: { website: 'localPartnerId', api: 'localPartnerIdApi' },
} as const;

interface DocumentProviderProps {
  commodityType: keyof typeof PARTNER_FIELDS;
}

/**
 * Lets a bank choose which partner's commodity trading forms/certificates apply
 * (e.g. Boubyan Capital), separately for the website and the API, per commodity type.
 * Leaving it unset (null) means Alshmokh forms are used.
 */
export function DocumentProvider({ commodityType }: DocumentProviderProps) {
  const dispatch = useAppDispatch();
  const account = useAppSelector(state => state.authentication.account);
  const bankEntity = useAppSelector(state => state.bank.entity);
  const partnerList = useAppSelector(state => state.partner.entities);

  const userRoleAccessList = useAppSelector(state => state.userManagement.userRoleAccessList);
  const isUserAuthorizedToEdit = userRoleAccessList.length === 0 || userRoleAccessList.some(role => role.bankId === bankEntity?.id);

  const fields = PARTNER_FIELDS[commodityType];
  const idPrefix = commodityType.toLowerCase();

  const [collapse, setCollapse] = useState(false);
  const [selectedPartnerId, setSelectedPartnerId] = useState('');
  const [selectedPartnerIdApi, setSelectedPartnerIdApi] = useState('');

  const isDisabled =
    (account?.authorities?.includes(AUTHORITIES.ALSHMOKH_USER) && !account?.authorities?.includes(AUTHORITIES.SUBADMIN_BANK)) ||
    !isUserAuthorizedToEdit;

  useEffect(() => {
    dispatch(getPartners({ page: 0, size: 1000, sort: 'name,asc' }));
  }, []);

  useEffect(() => {
    if (bankEntity?.id !== undefined) {
      setSelectedPartnerId(bankEntity?.[fields.website] != null ? String(bankEntity[fields.website]) : '');
      setSelectedPartnerIdApi(bankEntity?.[fields.api] != null ? String(bankEntity[fields.api]) : '');
    }
  }, [bankEntity, fields]);

  const handleCollapseToggle = () => {
    setCollapse(prev => !prev);
  };

  const handlePartnerChange = event => {
    setSelectedPartnerId(event.target.value);
  };

  const handlePartnerApiChange = event => {
    setSelectedPartnerIdApi(event.target.value);
  };

  const handleSubmit = () => {
    const updatedBank = {
      ...bankEntity,
      [fields.website]: selectedPartnerId ? Number(selectedPartnerId) : null,
      [fields.api]: selectedPartnerIdApi ? Number(selectedPartnerIdApi) : null,
    };
    dispatch(updateBank(updatedBank));
  };

  return (
    <div className="collapse-section">
      <Grid container className="align-items-center">
        <Grid item xs={10} sm={11} md={11.5}>
          <Typography variant="h5" component="div" data-cy={`${idPrefix}DocumentProviderHeading`}>
            {translate('comexApp.bank.details.documentProvider.title')}
          </Typography>
        </Grid>
        <Grid item xs={2} sm={1} md={0.5}>
          <IconButton aria-label="collapse" onClick={handleCollapseToggle}>
            {collapse ? <ExpandLessRoundedIcon /> : <ExpandMoreRoundedIcon />}
          </IconButton>
        </Grid>
      </Grid>
      <Collapse in={collapse}>
        <br />
        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, columnGap: 3 }}>
          <FormControl sx={{ width: { md: 300, xs: '100%' }, display: 'flex', justifyContent: 'flex-start', mb: 2 }}>
            <Label for={`${idPrefix}-partner-select`}>
              <Translate contentKey="comexApp.bank.details.documentProvider.website" />
            </Label>
            <Input
              type="select"
              id={`${idPrefix}-partner-select`}
              name={fields.website}
              data-cy={fields.website}
              value={selectedPartnerId}
              onChange={handlePartnerChange}
              disabled={isDisabled}
            >
              <option value="" label={translate('comexApp.bank.details.documentProvider.alshmokhDefaultOption')} />
              {partnerList.map(partner => (
                <option value={partner?.id} key={partner?.id}>
                  {partner?.name}
                </option>
              ))}
            </Input>
          </FormControl>
          <FormControl sx={{ width: { md: 300, xs: '100%' }, display: 'flex', justifyContent: 'flex-start', mb: 2 }}>
            <Label for={`${idPrefix}-partner-api-select`}>
              <Translate contentKey="comexApp.bank.details.documentProvider.api" />
            </Label>
            <Input
              type="select"
              id={`${idPrefix}-partner-api-select`}
              name={fields.api}
              data-cy={fields.api}
              value={selectedPartnerIdApi}
              onChange={handlePartnerApiChange}
              disabled={isDisabled}
            >
              <option value="" label={translate('comexApp.bank.details.documentProvider.alshmokhDefaultOption')} />
              {partnerList.map(partner => (
                <option value={partner?.id} key={partner?.id}>
                  {partner?.name}
                </option>
              ))}
            </Input>
          </FormControl>
        </Box>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Button color="primary" onClick={handleSubmit} value={0} disabled={isDisabled}>
            <FontAwesomeIcon icon="plus" /> <Translate contentKey="comexApp.bank.details.update" />
          </Button>
        </Box>
      </Collapse>
    </div>
  );
}
